import React, { useState } from 'react';
import Modal from 'react-bootstrap/Modal';
import Form from 'react-bootstrap/Form';
import Button from 'react-bootstrap/Button';
import Row from 'react-bootstrap/Row';
import Col from 'react-bootstrap/Col';
import Loader from "components/Loader";
import VoiceProfile from 'components/LM/Voice/Profile';
import VoiceMedia from 'components/LM/Voice/Media';
import VoiceUrl from 'components/LM/Voice/Url';
import FormCheckSquare from 'components/LM/common/FormCheckSquare';
import ListGroup from 'react-bootstrap/ListGroup';
import {useK2Loader, useK2BulkLoader} from 'hooks/useK2Loader';
import { ExternalLink } from 'react-feather';

const SEARCH_MESSAGE = {
	socialmedia: {
		header: "Enter the full URL of the profile you want to add (one URL per line, max 5 URLs):",
		label: "Url"
	},
	print: {
		header: "Insert media names or IDs (one per line) and list of medias will be listed",
		label: "Media name or Media ID"
	},
	online: {
		header: "Insert media names, IDs or URLs (one per line) and list of medias will be listed",
		label: "Media name, Media ID or URL"
	},
	url: {
		header: "Insert URLs (one per line) that you want to insert as voice URL relation",
		label: "Url"
	}
};

const SEARCH_ENDPOINTS = {
	'print': '/media/print/search',
	'online': '/media/online/search',
	'socialmedia': '/socialmedia/search'
};

function ModalSearchItem(props) {
	const { show, onClose, onAdd, voiceId, voiceType, ...modalProps } = props;
	const [type, setType] = useState();
	const [value, setValue] = useState();
	const [previousValue, setPreviousValue] = useState();

	const handleClose = (type, items) => {
		if (type === undefined) {
			const confirmed = window.confirm("Are you sure to close this window?");
			if (!confirmed) return;
		}
  
		const asyncOnAddRelation = async (t, p) => onAdd(t, p);
		if (type && items) asyncOnAddRelation(type, items);
		onClose();
		setType(undefined);
		setValue(undefined);
		setPreviousValue(undefined);
	};

	return (
		<Modal {...modalProps} show={show} onHide={handleClose} scrollable={true} backdrop="static">
			<StepFactory
				type={type}
				setType={setType}
				value={value}
				previousValue={previousValue}
				setValue={setValue}
				setPreviousValue={setPreviousValue}
				handleClose={handleClose}
				voiceId={voiceId}
				voiceType={voiceType}
			/>
		</Modal>
	)
}

function StepFactory({ type, setType, value, previousValue, setValue, setPreviousValue, handleClose, voiceId, voiceType }) {
	if (!type) {
		return <StepSelectType
			onSetType={setType}
			handleClose={handleClose}
		/>
	} else if (!value) {
		return <StepInputValue
			onSetValue={setValue}
			onSetPreviousValue={setPreviousValue}
			onSetType={setType}
			handleClose={handleClose}
			message={SEARCH_MESSAGE[type]}
			previousValue={previousValue}
			type={type}

		/>;
	} else {
		const searchProps = { setValue, handleClose, type, query: value, voiceId, voiceType };
		return <StepSearchValue {...searchProps} />
	}
}

function StepSelectType(props) {
	const { onSetType, handleClose } = props;
	const [type, setType] = useState("socialmedia");

	const nextButton = <NextButton onClick={() => onSetType(type)} />

	return (
		<Step
			stepNumber="1"
			handleClose={handleClose}
			button={nextButton}
		>
			<Form.Group>
				<Form.Label>Type</Form.Label>
				<Form.Control as="select" onChange={(ev) => setType(ev.target.value)}>
					<option value="socialmedia">Social (Twitter, Instagram, Youtube, Tiktok, Facebook, Weibo, Douyin, Xiaohongshu, Bilibili, Wechat)</option>
					<option value="print">Print</option>
					<option value="online">Online</option>
					<option value="url">Url</option>
				</Form.Control>
			</Form.Group>
		</Step>
	)
}

function isValidUrl(url) {
	const urlRegex = /^(https?):\/\/(([a-zA-Z0-9_-]+\.)+[a-zA-Z]{2,})(:\d+)?(\/[^\s]*)?$/;
	return urlRegex.test(url);
}

function StepInputValue(props) {
	const {
		onSetValue,
		onSetPreviousValue,
		onSetType,
		handleClose,
		message,
		previousValue,
		type,
	} = props;

	const [value, setValue] = useState(previousValue ?? '');
	const [errorMessage, setErrorMessage] = useState('');
	
	const isUrlType = ['url'].includes(type);
	const isTextarea = ['socialmedia'].includes(type);
	const isValid = value !== '' && !errorMessage;

	const MAX_ALLOWED_SEARCHES = 5;

	const validateTextareaUrls = (value) => {
		const lines = value
			.split('\n')
			.map(line => line.trim())
			.filter(Boolean);

		if (lines.length > MAX_ALLOWED_SEARCHES) {
			return `You can enter up to ${MAX_ALLOWED_SEARCHES} URLs.`;
		}

		const invalidLines = lines.filter(line => {
			return !isValidUrl(line);
		});

		if (invalidLines.length > 0) {
			return `Invalid URLs detected: ${invalidLines.join(', ')}`;
		}

		return '';
	};

	const handleChange = (ev) => {
		const inputValue = ev.target.value;
		setValue(inputValue);

		if (isTextarea) {
			setErrorMessage(validateTextareaUrls(inputValue));
			return;
		}

		if (isUrlType) {
			setErrorMessage(validateTextareaUrls(inputValue));
			return;
		}
		setErrorMessage('');
	};

	const handleNext = () => {
		onSetValue(value);
		onSetPreviousValue(value);
	};
	const handlePrevious = () => {
		onSetType(null);
		onSetPreviousValue(null);
	};

	return (
		<Step
			stepNumber="2"
			handleClose={handleClose}
			button={<NextButton disabled={!isValid || value === ''} onClick={handleNext} />}
			previous={<PreviousButton onClick={handlePrevious} />}
		>
			<>
				<p>
					{message.header}
				</p>

				<Form.Group>
					<Form.Label>{message.label}</Form.Label>
					<Form.Control
						as={'textarea'}
						rows={5}
						type={isUrlType ? 'url' : 'text'}
						placeholder={isUrlType ? 'https://google.com' : ''}
						value={value}
						onChange={handleChange}
						isInvalid={Boolean(errorMessage)}
					/>
					<Form.Control.Feedback type="invalid">
						{errorMessage}
					</Form.Control.Feedback>
				</Form.Group>
			</>
		</Step>
	);
}


function StepSearchValue(props) {
	const { type, query, voiceId, voiceType } = props;
	const { setValue, handleClose } = props;
	const [entities, setEntities] = useState([]);

	const onToggleEntity = (entity, entityType, enabled) => {
		if (enabled) {
			setEntities([...entities, { type: entityType, result: entity }]);
		} else {
			setEntities(entities.filter(e => e.result.id !== entity.id))
		}
	}

	let body, button;
	const buildVoiceUrl = (urlsQuery = [], voiceId, error) => {
		const urls = (Array.isArray(urlsQuery) ? urlsQuery : [urlsQuery])
			.filter(Boolean)
			.map((item) => ({
				id: item,
				url: item,
				voiceId,
				type: "other"
			}));

		body = (
			<div>
				{error && (
					<>
						<h4>Error</h4>
						<p>{handlerError(error)}</p>
					</>
				)}
				{urls.map((urlObj) => (
					<div key={urlObj.id} className="mb-3 mt-1">
						<VoiceUrl url={urlObj} type="other" />
					</div>
				))}
			</div>
		);

		button = (
			<Button variant="warning" onClick={() => handleClose("other", urls)}>
				Insert as URL
			</Button>
		);
	};

	const cleanQuery = (query) => {
		return [...new Set(query.split("\n").filter(term => term !== ""))];
	}

	const useK2Api = (type, query) => {
		let loadRequests = [];
		if (type === 'socialmedia' || areMediaTypes(type)) {
			const urls = cleanQuery(query);
			loadRequests = urls.map(url => prepareSearchRequest(type, url));
		}
		
		// eslint-disable-next-line react-hooks/rules-of-hooks
		return useK2BulkLoader(loadRequests);
	}

	if (type === 'url') {
		const urls = cleanQuery(query);
		buildVoiceUrl(urls, voiceId);
	} else {
		// eslint-disable-next-line react-hooks/rules-of-hooks
		const [loading, response, error] = useK2Api(type, query);
		let loadingLabels;
		let allowedLabels;
		if (type === 'socialmedia') {
			// eslint-disable-next-line react-hooks/rules-of-hooks
			const [loading, labels] = useK2Loader('/labels');
			if (loading) {
				loadingLabels = loading
			} else {
				const labelsGroupedByAllowType = groupLabelsByAllowedTypes(labels);
				allowedLabels = labelsGroupedByAllowType[voiceType].map(label => label.name);
			};
		}

		if (loading || loadingLabels) {
			body = <Loader />;
		} else if (response) {
			const normalizedResponse = response.flatMap(data => {
				if (data?.success === false) { return [data]; }
				const list = Array.isArray(data?.result) ? data.result : (Array.isArray(data) ? data : []);
				return list.map(entity => ({ ...entity, profile_type: data.type }));
			});
			const normalizedEntityType = response[0]?.type || type;

			if (normalizedResponse.length === 0) {
				body = <p>No results found</p>;
			} else {
				body = <VoiceEntities response={normalizedResponse} type={normalizedEntityType} voiceAllowedLabels={allowedLabels} voiceId={voiceId} onToggle={onToggleEntity} />
			}

			button = (<Button
				variant="success"
				onClick={() => handleClose(normalizedEntityType, entities.map(e => { return e.result }))}
				disabled={!entities.length}>Insert relation</Button>);
		} else {
			if (type !== 'print') buildVoiceUrl(query, voiceId, error);
		}
	}

	const previousButton = <PreviousButton onClick={() => setValue(null)} />

	return (
		<Step
			stepNumber="3"
			handleClose={handleClose}
			button={button}
			previous={previousButton}
		>
			{body}
		</Step>
	)
}

function prepareSearchRequest(type, query) {
	const endpoint = SEARCH_ENDPOINTS[type];
	const params = { params: areMediaTypes(type) ? { query: query } : { url: query } };

	return { endpoint, params };
}

function areMediaTypes(type) {
	return (type === 'print' || type === 'online');
}

function VoiceEntities(props) {
	const { response, voiceId, voiceAllowedLabels } = props;
	let { type } = props;
	const { onToggle } = props;

	return response.map(entity => {
		if (entity?.success === false) {
			const url = {
				id: entity.params.params.url,
				url: entity.params.params.url,
				voiceId: voiceId,
				type: handlerError(entity.error)
			};
			return (
				<ListGroup className="new-item mb-2" key={`${type}_${entity.params.params.url}`}>
					{
						<Row className="mb-3 mt-1">
							<Col md="auto" className="d-flex align-items-center">
								<FormCheckSquare className="ml-2 mr-2" width="18" disabled={true} />
							</Col>
							<Col className="ml-n2">
								<VoiceUrl url={url} type="other" />
							</Col>
						</Row>
					}
				</ListGroup>
			);
		}
		const isTakenByAnyVoice = entity.voice !== null && entity.voice.id !== null;
		const isTakenByCurrentVoice = isTakenByAnyVoice && entity.voice.id === voiceId;
		type = entity.profile_type || type;
		if (isTakenByCurrentVoice) {
			entity.warning = "Already linked to this voice";
		} else if (isTakenByAnyVoice && !isTakenByCurrentVoice) {
			entity.warning = TakenVoice(entity.voice.id);
		} else if (voiceAllowedLabels && voiceAllowedLabels.length) {
			const entityLabels = entity.labels.map(label => label.name);
			const hasIncompatibleLabels = entityLabels.filter(entityLabel => !voiceAllowedLabels.includes(entityLabel));
			if (hasIncompatibleLabels.length) {
				entity.warning = `The profile has incompatible labels: ${hasIncompatibleLabels.join(',')}, once linked it will lose them`;
			}
		}

		const commonComponentProps = {
			type,
			checked: isTakenByCurrentVoice,
			editMode: true,
			editType: true,
			disabled: isTakenByAnyVoice,
			onToggle: (checked) => onToggle(entity, type, checked)
		};

		return (
			<ListGroup className="new-item mb-2" key={`${type}_${entity.id}`}>
				{
					(areMediaTypes(type)) ?
						<VoiceMedia media={entity} {...commonComponentProps} /> : <VoiceProfile profile={{ ...entity, type }} {...commonComponentProps} />
				}
			</ListGroup>
		);
	});
}

function TakenVoice(voiceId) {
	const voiceLink = <a className="text-warning" href={`/voices/${voiceId}`} target="_blank" rel="noopener noreferrer">{voiceId} <ExternalLink className="feather" /></a>;

	return <>Taken by voice id: {voiceLink}</>
}

function NextButton(props) {
	return (
		<Button {...props} variant="primary">
			Next
		</Button>);
}

function PreviousButton(props) {
	return (
		<Button {...props} variant="primary">
			Previous
		</Button>);
}

function Step(props) {
	const { button, previous, stepNumber } = props;
	const { handleClose } = props;
	const body = props.children;

	return (
		<>
			<Modal.Header>
				<Modal.Title className="h5 font-weight-bold">Insert new relation (step {stepNumber} of 3)</Modal.Title>
			</Modal.Header>
			<Modal.Body>{body}</Modal.Body>
			<Modal.Footer>
				<Button className='mr-auto' variant="secondary" onClick={() => handleClose()}>
					Close
				</Button>
				{previous}
				{button}
			</Modal.Footer>
		</>
	);
}

function handlerError(error) {
	let errorMessage;
	if (error.response) {
		const code = error.response.status;
		if (code === 400) {
			errorMessage = 'The system does not recognize the provided url or is not valid';
		} else if (code === 403) {
			errorMessage = 'The profile is not retrievable from the API, due to is a restricted or protected user';
		} else if (code === 404) {
			errorMessage = 'The profile does not exists';
		} else if (code === 429) {
			errorMessage = 'Error. Too many requests, can\'t get user data. Please try again later.';
		} else if (500 <= code && code <= 599) {
			errorMessage = 'There was an error trying to find the profile, please retry later';
		} else {
			errorMessage = error.message
		}
	} else {
		errorMessage = error.message
	}
	return errorMessage;
}

function groupLabelsByAllowedTypes(labels) {
	if (!labels) return;
	const dict = {};
	labels.forEach(label => {
		label.allowed_types.forEach(type => {
			if (!dict[type]) {
				dict[type] = [label];
			} else {
				dict[type].push(label);
			}
		})
	});

	return dict;
}

export default ModalSearchItem;
